import debugDispatcher from '../utils/debugDispatcher';
import { getValueByPath, setValueByPath } from '../utils/util';
import shortcutController from './shortcutController';

let currentConfigurations = window.config as unknown as Record<string, unknown>;
const configurationPossibleValues = {};

type Config = {
  common: {
    timing: {
      formsValidationControlTimeout: number;
      keyboardFilterDebounceTime: number;
      notificationVibrateTime: number;
    };
  };
  components: {
    tabs: {
      nextKey: string; // KeyCode
      prevKey: string; // KeyCode
    };
    uploader: {
      showSuccessMessage: boolean;
      view: 'menu' | 'default'; // menu | default
    };
  };
  execution: {
    components: {
      dataGrid: {
        hideRowDeleteButton?: boolean;
        heightAuto: boolean;
        gridView?: 'default' | 'small';
        showDeleteButton: boolean;
        deleteButtonIcon: 'Trash' | 'Close'; // Trash | Close
      };
      gridEditionModal: {
        cancellable: boolean;
      };
    };
  };
  lists: {
    defaultMaximized: boolean;
    persistMaximized: boolean;
    priorityIcon: 'SquareFilled' | 'SquareOutline'; // SquareFilled | SquareOutline
  };
  main: {
    tabs: {
      showArrows: 'onlyWhenOverflow';
    };
  };
};

type TObjectInspection<T, Prefix extends string = ''> = T extends object
  ? {
      [K in keyof T]-?: NonNullable<T[K]> extends object
        ? TObjectInspection<NonNullable<T[K]>, `${Prefix}${K & string}.`>
        : `${Prefix}${K & string}`;
    }[keyof T]
  : never;

type TPathValue<
  T,
  Path extends string,
> = Path extends `${infer Key}.${infer Rest}`
  ? Key extends keyof T
    ? TPathValue<T[Key], Rest>
    : never
  : Path extends keyof T
    ? T[Path]
    : never;

debugDispatcher.on(
  'config',
  ([value]) => {
    if (value) console.info(conf(value as any, ''));
    else
      console.info({
        currentConfigurations,
        configurationPossibleValues,
      });
  },
  'Muestra el árbol de configuración actual',
);

shortcutController.on(
  'config'.split(''),
  () => {
    console.info({
      currentConfigurations,
      configurationPossibleValues,
    });
  },
  'dev',
);

type NoInfer<T> = [T][T extends any ? 0 : never];

/**
 * Trae un valor de configuración para el path establecido
 *
 * @param path Path de la configuración con formato de acceso de objetos (step.step2.step3.prop)
 * @param defaultValue Un valor que será usado en caso de no encontrarse la configuración buscada.
 * @returns El valor de la configuración si existe, defaultValue en caso contrario.
 */
export default function conf<
  Path extends string = TObjectInspection<Config>,
  ValueType extends TPathValue<Config, Path> = TPathValue<Config, Path>,
>(path: Path, defaultValue?: NoInfer<ValueType>): ValueType {
  const currentValue = getValueByPath(
    window.config as unknown as Record<string, unknown>,
    path,
  );

  const newConfigurations = setValueByPath(
    currentConfigurations,
    path,
    currentValue ?? defaultValue,
  );

  if (newConfigurations) currentConfigurations = newConfigurations;
  else {
    console.info({
      currentConfigurations,
      path,
    });
    throw new Error(
      `There is a configuration which make the configurations tree crash. Please see ${path}`,
    );
  }

  return (currentValue ?? defaultValue) as ValueType;
}
